import { useState, useCallback, useEffect } from 'react'
import update from 'immutability-helper'
import { Redirect } from 'react-router-dom'
import useGame from './hooks/useGame'
import { useSelector } from '../../hooks/useSelector'
import usePlayerActions from '../SheetView/hooks/usePlayerActions'
import useRouting from '../../hooks/useRouting'
import { GameOptions } from '@prometheus/types'
import { useJoinGameRoom } from '../../hooks/useJoinGameRoom'
import { useDispatch } from 'react-redux'
import { setGame } from '../../reducers/game/game'
import { DIFFICULTY_PRESETS, difficultyPresetOptions } from '@prometheus/utils'
import { Box, Button, Card, CardBody, CardFooter, CardHeader, Checkbox, Dropdown, Input, Modal, Stack, Table, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'

export default () => {
  const uid = useSelector(s => s.auth.uid)
  const {
    game,
    onStart,
  } = useGame()

  useJoinGameRoom()

  const _options = useSelector(s => s.game.options)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [options, setOptions] = useState<any>({})
  const [dirty, setDirty] = useState<boolean>(false)
  const owner_id = useSelector(s => s.game.game?.owner)

  const disabled = owner_id !== uid

  useEffect(() => {
    if (_options?.tutorial) {
      const to = setTimeout(() => onStart(), 10)
      return () => clearTimeout(to)
    }
  }, [_options?.tutorial])

  const onChange = useCallback((e) => {
    // const { name, value } = data
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      setOptions((o: any) => update(o, {
        [name]: { $set: checked },
      }))
    } else if (name === 'difficulty_settings') {
      // One source of truth for the difficulty curve: the headless simulator
      // dials the same knobs, so a balance run can never be measuring a game
      // the lobby doesn't actually create.
      const preset = difficultyPresetOptions(Number(value))
      setOptions((o: any) => update(o, Object.fromEntries(
        Object.entries(preset).map(([key, presetValue]) => [key, { $set: presetValue }]),
      )))
    } else {
      setOptions((o: any) => update(o, {
        [name]: { $set: Number(value) },
      }))
    }
    setDirty(true)
  }, [])

  const {
    onLeaveLobby,
    onDeleteGame,
    onSetGameOptions,
  } = usePlayerActions()

  const onSave = useCallback(async () => {
    await onSetGameOptions(options)
    setDirty(false)
    setOptions({})
  }, [options, onSetGameOptions])

  const { goBack } = useRouting()

  const dispatch = useDispatch()

  if (!game || !game.id) {
    return <div />
  }

  if (game.id && game.status === 'deleted') {
    setTimeout(() => {
      dispatch(setGame())
    })
    return <Redirect to="/games" />
  }

  if (game.status === 'active') {
    return <Redirect to={`/game/${game.id}`} />
  }

  const isOwner = game.owner === uid

  const buildField = (label: string, name: string, extra?: any) => {
    let value: any
    let input: any

    if (typeof (_options || {})[name as keyof GameOptions] === 'boolean') {
      if (typeof options[name] === 'boolean') {
        value = options[name]
      } else {
        value = (_options || {})[name as keyof GameOptions] ?? options[name]
      }
      input = (
        <Stack space="sm">
          <Typography variant="body2">{label}</Typography>
          <Checkbox
            label={label}
            checked={value}
            onChange={onChange}
            name={name}
            disabled={disabled || extra?.disabled}
          />
        </Stack>
      )
    } else if (name === 'difficulty_settings') {
      input = (
        <Dropdown
          style={{ width: '100%', maxWidth: '200px' }}
          placeholder='Choose preset difficulty'
          label={label}
          value={options['difficulty_settings'] ?? _options?.['difficulty_settings'] ?? 5}
          items={DIFFICULTY_PRESETS.map(i => ({ label: i.label, value: i.difficulty }))}
          onChange={(v) => onChange({ target: { value: v, name }})}
          disabled={disabled || extra?.disabled}
        />
      )
    } else {
      value = options[name] || (_options || {})[name as keyof GameOptions] || 0
      input = (
        <Input
          type="number"
          value={value}
          disabled={disabled}
          label={label}
          onChange={onChange}
          name={name}
          min={0}
          step={extra?.step || 1}
        />
      )
    }
    return (
      input
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, padding: '16px', boxSizing: 'border-box', overflowY: 'auto' }}>
      <Card style={{ width: '100%', maxWidth: '440px' }}>
        <CardHeader><Typography variant="h3" retro>Game Lobby</Typography></CardHeader>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Table
            style={{ width: '300px', maxWidth: '100%' }}
            striped
            columns={[{ key: 'name', label: 'Players' }]}
            data={game.players.map(({ name }) => ({ name }))}
          />
          <Stack style={{ flex: '1 1 180px' }}>
            <PromiseButtonV2 disabled={!isOwner} onClick={onStart}>Start Game</PromiseButtonV2>
            <PromiseButtonV2 disabled={!isOwner} onClick={onDeleteGame} variant="danger" outline>Close Game</PromiseButtonV2>
            <PromiseButtonV2 variant="dark" disabled={!isOwner} onClick={() => setSettingsOpen(true)}>Settings</PromiseButtonV2>
          </Stack>
        </div>
      </Card>

      {settingsOpen && (
        <Modal onClose={() => setSettingsOpen(false)}>
          <Card>
            <CardBody style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Box horizontal="base">
                <Stack space="sm">
                  <Typography variant="h4" retro color="danger">Difficulty Settings</Typography>
                  <Card interactive variant='elevated'>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Difficulty Preset', 'difficulty_settings')}
                      {buildField('Recession Length', 'difficulty_recession_length_multiplier')}
                      {buildField('Recession Revenue', 'difficulty_recession_revenue_multiplier')}
                      {buildField('Resource Requirements', 'difficulty_planet_resource_requirements')}
                      {buildField('Planet Upgrade Speed', 'planet_upgrade_speed')}
                      {buildField('Storms', 'difficulty_geomagnetic_storms')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Loans and Interest</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Credit Rating D/E', 'difficulty_credit_rating_impact')}
                      {buildField('Interest', 'config_base_prime_interest')}
                      {buildField('CR_Interest Spread', 'config_credit_rating_interest_spread')}
                      {buildField('Leverage', 'config_leverage_factor')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Game Configuration</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Disable Planet Tokens', 'disable_planet_tokens')}
                      {buildField('Planet Dice Roll', 'planet_rolls_count')}
                      {buildField('Trade Occurence', 'trade_occurence', { step: 0.01 })}
                      {buildField('Trade Sell Price', 'config_trade_sell_price_ratio')}
                      {buildField('Trade Buy Price', 'config_trade_buy_price_ratio')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Player Configuration</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Show Equity', 'config_show_equity')}
                      {buildField('Starting Cash', 'starting_cash')}
                      {buildField('Starting Population', 'starting_population')}
                      {buildField('Starting Bonds', 'starting_bonds')}
                      {buildField('Bond Spread', 'difficulty_bond_spread')}
                      {buildField('Starting LOC', 'starting_loc')}
                      {buildField('Mine Counts', 'config_mine_counts')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Random Planet Generation</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Random Planet Generation', 'random_planet_generation')}
                      {buildField('Random Planets Unlock Year', 'random_planets_access_year')}
                      {buildField('Bonus Random Quadrants Count', 'randomly_generated_quadrants')}
                      {buildField('Random Planets Per Quadrant (coming)', 'random_planets_per_quadrant')}
                      {buildField('Random Planets Unlock Interval', 'random_planets_unlock_interval')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Game Timer</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Disable Turn Timers', 'disable_timer')}
                      {buildField('Buy Phase Time', 'timer_buy_phase_duration')}
                      {buildField('Trade Phase Time', 'timer_trade_phase_duration')}
                      {buildField('Travel Phase Time', 'timer_travel_phase_duration')}
                      {buildField('Timer Refreshes', 'timer_refresh_charges')}
                      {buildField('Refresh Renewal', 'refresh_charge_renewal')}
                      {buildField('Time Gained', 'refresh_time_gained')}
                    </div>
                  </Card>
                  <Typography variant="h4" retro>Test</Typography>
                  <Card variant="elevated">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      {buildField('Boss Invaders (replaces Overlords)', 'boss_invaders_enabled')}
                      {buildField('Boss Start Year', 'boss_start_year')}
                      {buildField('Boss Spawn Probability', 'boss_spawn_probability', { step: 0.01 })}
                    </div>
                  </Card>
                </Stack>
              </Box>
            </CardBody>
            <CardFooter>
              <Button variant="dark" onClick={() => { setOptions({}); setSettingsOpen(false); }}>Cancel</Button>
              <PromiseButtonV2 disabled={!dirty} onClick={() => onSave().then(() => { setDirty(false); setSettingsOpen(false); })}>Save</PromiseButtonV2>
            </CardFooter>
          </Card>
        </Modal>
      )}
    </div>
  )
}

